import { useParams, Navigate } from "react-router-dom";
import { ArrowLeft, MessageCircleQuestion, Camera, Scale, Share2 } from "lucide-react";
import { motion } from "framer-motion";
import { getArticle } from "@/data/articles";
import { waLink, waShareLink, WA_DEMO_MSG } from "@/lib/wa";
import { STR } from "@/data/i18n";
import { useLang } from "@/lib/lang";
import { WhatsAppIcon } from "@/components/landing/Shared";

const ICONS = { MessageCircleQuestion, Camera, Scale };

const ShareButton = ({ article, testid, label, className = "" }) => {
    const { lang } = useLang();
    const shareUrl = `${window.location.origin}/tips/${article.slug}`;
    const text = `Tips dari TechFlaze: "${article.title[lang]}"\n${shareUrl}`;
    return (
        <a
            href={waShareLink(text)}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={testid}
            className={`inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-5 py-2.5 text-sm font-medium text-zinc-200 transition-colors duration-200 hover:border-green-500/60 hover:text-green-400 ${className}`}
        >
            <Share2 className="h-4 w-4" />
            {label}
        </a>
    );
};

const Block = ({ block }) => {
    if (block.type === "h2") {
        return (
            <h2 className="mt-10 text-xl font-bold text-slate-50 md:text-2xl">{block.text}</h2>
        );
    }
    return <p className="mt-5 text-base leading-relaxed text-zinc-300">{block.text}</p>;
};

export default function ArticlePage() {
    const { slug } = useParams();
    const article = getArticle(slug);
    const { lang } = useLang();
    const t = STR.article[lang];

    if (!article) return <Navigate to="/" replace />;

    const Icon = ICONS[article.icon] || MessageCircleQuestion;

    return (
        <main data-testid="article-page" className="pt-28 pb-24">
            <motion.article
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="mx-auto max-w-3xl px-5 md:px-8"
            >
                <a
                    href="/#blog"
                    data-testid="article-back-link"
                    className="inline-flex items-center gap-2 text-sm font-medium text-zinc-400 transition-colors duration-200 hover:text-amber-400"
                >
                    <ArrowLeft className="h-4 w-4" />
                    {t.back}
                </a>

                <div className="mt-8 flex h-44 items-center justify-center rounded-2xl bg-gradient-to-br from-red-600/30 via-orange-600/20 to-amber-500/20 md:h-56">
                    <Icon className="h-14 w-14 text-amber-400/80" />
                </div>

                <div className="mt-8 flex flex-wrap items-center justify-between gap-4">
                    <p className="text-xs font-semibold uppercase tracking-widest text-amber-400">
                        Blog & Tips · {article.date} · {article.readTime[lang]}
                    </p>
                    <ShareButton article={article} testid="article-share-top" label={t.share} />
                </div>
                <h1 className="mt-4 text-3xl font-extrabold leading-tight tracking-tight text-slate-50 md:text-4xl">
                    {article.title[lang]}
                </h1>

                <div className="mt-6">
                    {article.content[lang].map((block, i) => (
                        <Block key={i} block={block} />
                    ))}
                </div>

                <div
                    data-testid="article-cta"
                    className="mt-14 rounded-2xl border border-orange-500/40 bg-[#161210] p-8 text-center"
                >
                    <h2 className="text-xl font-bold text-slate-50">{t.ctaTitle}</h2>
                    <p className="mx-auto mt-3 max-w-md text-sm leading-relaxed text-zinc-400">
                        {t.ctaDesc}
                    </p>
                    <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
                        <a
                            href={waLink(WA_DEMO_MSG)}
                            target="_blank"
                            rel="noopener noreferrer"
                            data-testid="article-cta-wa-button"
                            className="btn-flame inline-flex items-center justify-center gap-2 rounded-full px-8 py-3.5 text-sm font-semibold text-white shadow-lg shadow-orange-900/40 transition-transform duration-200 hover:scale-105"
                        >
                            <WhatsAppIcon className="h-4 w-4" />
                            {t.ctaBtn}
                        </a>
                        <ShareButton article={article} testid="article-share-bottom" label={t.share} />
                    </div>
                </div>
            </motion.article>
        </main>
    );
}
